import React, { useState, useEffect } from 'react';
import {
  Box, IconButton, Menu, MenuItem, ListItemIcon,
  Divider, Avatar, Badge, Typography,
} from '@mui/material';
import { getCvBlob } from '../../../services/FileService';
import { styled } from '@mui/material/styles';
import Logout from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getUserData } from '../../../utils/FunctionHelpers';
import { logout } from '../../../services/AuthentificationService';
import { useHistory } from 'react-router-dom';
import NotificationsPopover from '../../../components/NotificationPopover/NotificationPopover';

const OnlineBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#10b981',
    color: '#10b981',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    width: 10,
    height: 10,
    borderRadius: '50%',
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      border: '1px solid currentColor',
      content: '""',
    },
  },
}));

const UserChip = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '5px 10px 5px 5px',
  borderRadius: 12,
  cursor: 'pointer',
  border: '1px solid #e2e8f0',
  transition: 'all 0.18s ease',
  '&:hover': {
    background: '#f8fafc',
    borderColor: '#cbd5e1',
  },
}));

const UserBoxContent = ({ isMobile = false, notifications, setNotifications }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const history = useHistory();
  const open = Boolean(anchorEl);
  const currentUser = getUserData();

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      history.push('/login');
      await logout();
    } catch {}
  };

  const handleGetImage = async (imageName) => {
    try {
      const imageFile = (await getCvBlob(imageName)).data;
      const reader = new FileReader();
      reader.readAsDataURL(imageFile);
      reader.onloadend = () => setImageUrl(reader.result);
    } catch {}
  };

  const getUserInitials = () => {
    const first = currentUser?.firstname?.[0]?.toUpperCase() ?? '';
    const last = currentUser?.lastname?.[0]?.toUpperCase() ?? '';
    return `${first}${last}`.trim();
  };

  useEffect(() => {
    if (currentUser?.image) handleGetImage(currentUser.image);
  }, []);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <NotificationsPopover notificationsArray={notifications} setNotifications={setNotifications} />

      <UserChip onClick={handleClick}>
        <OnlineBadge
          overlap="circular"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          variant="dot"
        >
          <Avatar
            sx={{
              width: 34,
              height: 34,
              background: 'linear-gradient(135deg, #0556a4, #03407b)',
              fontSize: '1rem',
              fontWeight: 700,
            }}
          >
            {!currentUser?.image
              ? getUserInitials()
              : <img width="100%" src={imageUrl} alt="" />}
          </Avatar>
        </OnlineBadge>

        {!isMobile && (
          <Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', lineHeight: 1.2 }}>
              {currentUser?.firstname} {currentUser?.lastname}
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: '#94a3b8', lineHeight: 1.2 }}>
              {currentUser?.fonction}
            </Typography>
          </Box>
        )}

        <KeyboardArrowDownIcon
          sx={{ fontSize: 16, color: '#94a3b8', transition: 'transform 0.18s', transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </UserChip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: {
          elevation: 0,
          sx: {
            mt: 1,
            minWidth: 220,
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            overflow: 'visible',
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 20,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              borderTop: '1px solid #e2e8f0',
              borderLeft: '1px solid #e2e8f0',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 38, height: 38,
              background: 'linear-gradient(135deg, #0556a4, #03407b)',
              fontSize: '1rem', fontWeight: 700,
            }}
          >
            {!currentUser?.image ? getUserInitials() : <img width="100%" src={imageUrl} alt="" />}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
              {currentUser?.firstname} {currentUser?.lastname}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
              {currentUser?.email}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: '#f1f5f9' }} />

        <MenuItem
          onClick={handleLogout}
          sx={{
            mx: 1, my: 0.5, borderRadius: 2,
            color: '#ef4444', fontSize: '1rem',
            '&:hover': { background: '#fef2f2' },
          }}
        >
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: '#ef4444' }} />
          </ListItemIcon>
          Se déconnecter
        </MenuItem>
      </Menu>
    </Box>
  );
};

const UserBox = ({ notifications, setNotifications }) => (
  <UserBoxContent notifications={notifications} setNotifications={setNotifications} />
);

const UserBoxMdMode = ({ notifications, setNotifications }) => (
  <UserBoxContent isMobile={true} notifications={notifications} setNotifications={setNotifications} />
);

export default UserBox;
export { UserBoxMdMode };
